import { datadogRum } from "@datadog/browser-rum";
import * as Sentry from "@sentry/react";
import queryString from "query-string";
import { Suspense } from "react";
import ReactDOM from "react-dom";
import { MemoryRouter, Route, type RouteComponentProps, type StaticContext } from "react-router";
import { CompatRouter } from "react-router-dom-v5-compat";

import { AppProvider } from "helpers/AppProvider";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { RootForegroundNotificiationController } from "helpers/pushNotification.utilities";

import { BTSessionStorage } from "types/btStorage";

import "utilities/api/setupApix";
// Intentionally adding this here so storybook doesn't load. That way if everything works in storybook, we can just delete this when webforms is gone (in 2050)
import { getAppDefaultFlagValue, getAppDefaultFlagValueAsBoolean } from "utilities/appDefaultFlags";
import {
    getCurrentEnvironment,
    getCurrentSentryEnvironment,
} from "utilities/environment/environment";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { isNullOrUndefined } from "utilities/object/object";
import { lazyLoadWithRetry } from "utilities/react.utilities";
import { routes } from "utilities/routes";
import "utilities/webview/setupMobileMock";

import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import EntityListWrapper from "commonComponents/helpers/EntityListWrapper/EntityListWrapper";
import { ErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { registerDragAndDropPlugins } from "commonComponents/utilities/DragAndDrop/DragAndDrop";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { RootFocusObserver } from "commonComponents/utilities/Focus/RootFocusObserver";
import { OwnerHeaderInfo } from "commonComponents/utilities/HeaderInfo/OwnerHeaderInfo";
import { MainNavigation } from "commonComponents/utilities/MainNavigation/MainNavigation";
import { MainNavigationWrapper } from "commonComponents/utilities/MainNavigation/MainNavigationWrapper";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";

import RedirectForCredentialUpgrade from "entity/login/RedirectForCredentialUpgrade/RedirectForCredentialUpgrade";

import "./styles/webformsCssHacks.less";

import { App } from "./App";

const AdminBuildersImpersonationQR = lazyLoadComponent(
    () => import("entity/admin/AdminBuilders/AdminBuildersImpersonationQR"),
);

const TradeAgreement = lazyLoadWithRetry(
    () => import("entity/tradeAgreement/TradeAgreement/TradeAgreement"),
);
const JobPickerWrapper = lazyLoadWithRetry(
    () => import("commonComponents/utilities/JobPicker/JobPickerWrapper"),
);
const HeaderAlerter = lazyLoadWithRetry(
    () => import("./commonComponents/utilities/HeaderAlerter/HeaderAlerter"),
);
const BTAdminWebformsMenu = lazyLoadWithRetry(() => import("./layouts/BTAdminWebformsMenu"));

const ModalLauncher = lazyLoadComponent(
    () => import("commonComponents/utilities/ModalLauncher/ModalLauncher"),
);
const AdminBuildersSearch = lazyLoadComponent(
    () => import("entity/builderSearch/AdminBuildersSearch"),
);
const BtAdminContactsTable = lazyLoadComponent(
    () => import("entity/contact/BtAdmin/BtAdminContactsTable"),
);

window.rootFocusObserver = new RootFocusObserver();
window.rootForegroundNotificationController = new RootForegroundNotificiationController();

const environment: string = getCurrentEnvironment();

const enableRUM = getAppDefaultFlagValueAsBoolean("dataDogRUM");
const enableTracing = getAppDefaultFlagValueAsBoolean("dataDogEnableTracing");
const ddAppId = getAppDefaultFlagValue("dataDogApplicationId");
const ddToken = getAppDefaultFlagValue("dataDogClientToken");

// Benign browser errors that aren't actionable app bugs — filtered from both
// RUM and Sentry to reduce noise. NetworkError is broad so only Sentry ignores it.
const commonIgnoredErrors = [
    "ResizeObserver loop",
    // Salesforce ESW chat bundle: deferred callbacks reference an
    // `embeddedservice_bootstrap` global that is gone by the time the
    // user has logged out/navigated away. The SDK is minified and
    // can't be patched.
    "embeddedservice_bootstrap",
    // Microsoft Word saves documents as HTML with onclick handlers that call
    // msoCommentHide / msoCommentShow. When Word-generated HTML is rendered
    // in BT (e.g. rich text fields), clicking comment annotations fires these
    // ReferenceErrors. The functions are Office-internal and can't be stubbed.
    "msoComment",
];

if (enableRUM && ddAppId && ddToken) {
    datadogRum.init({
        applicationId: ddAppId,
        clientToken: ddToken,
        site: getAppDefaultFlagValue("dataDogSite"),
        service: "buildertrend.net",
        env: environment,
        sessionSampleRate: getAppDefaultFlagValue("dataDogSessionSampleRate"),
        sessionReplaySampleRate: getAppDefaultFlagValue("dataDogPremiumSampleRate"), // if not included, the default is 100
        trackUserInteractions: getAppDefaultFlagValueAsBoolean("dataDogTrackUserInteractions"),
        trackResources: true,
        trackLongTasks: true,
        allowedTracingUrls: enableTracing ? [/https:\/\/.*buildertrend\.net/] : undefined,
        defaultPrivacyLevel: "mask-user-input",
        version: window.btJScriptGlobals.sentryRelease,
        enableExperimentalFeatures: ["feature_flags"],
        beforeSend: (event) => {
            if (
                event.type === "error" &&
                commonIgnoredErrors.some((msg) => event.error?.message?.includes(msg))
            ) {
                return false;
            }
            return true;
        },
    });
    datadogRum.startSessionReplayRecording();
}

const dsnVal = window.btJScriptGlobals.appDefaultFlags.sentryReactDSN;
const enableSentry = getAppDefaultFlagValueAsBoolean("enableSentry");
const sentryEnvironment: string = getCurrentSentryEnvironment();

if (dsnVal && enableSentry) {
    const tracesSampleRateString = window.btJScriptGlobals.appDefaultFlags
        .sentryReactSamplingRate as string;
    const tracesSampleRate = parseFloat(tracesSampleRateString) || 0;

    const integrations: any[] = [
        Sentry.browserTracingIntegration({
            beforeStartSpan: (context) => {
                context.name = location.pathname
                    .replace(/\/[a-f0-9]{32}/g, "/<hash>")
                    .replace(/\/\d+/g, "/<digits>");
                return context;
            },
        }),
        Sentry.httpClientIntegration(),
    ];

    Sentry.init({
        dsn: dsnVal,
        environment: sentryEnvironment,
        ignoreErrors: [...commonIgnoredErrors, "NetworkError"],
        denyUrls: [/maps\.googleapis\.com\/maps-api-v3\//, /\/ESWBuildertrendChat\d+\//],
        integrations: integrations,
        tracesSampleRate: tracesSampleRate,
        beforeBreadcrumb: (breadcrumb, hint) => {
            if (breadcrumb.category === "ui.click" && hint && hint.event) {
                const { target } = hint.event;
                if (target && target.dataset && target.dataset.testid) {
                    breadcrumb.message += ` data-testid['${target.dataset.testid}']`;
                }
            }

            return breadcrumb;
        },
        release: window.btJScriptGlobals.sentryRelease,
    });
}

// Register SortableJS Plugins
registerDragAndDropPlugins();

// path starts with /app/
const isSinglePageApp = window.location.pathname.toLowerCase().startsWith("/app");
const reactMainNavigation = document.getElementById("reactMainNavigation");
const reactHeaderInfo = document.getElementById("reactHeaderInfo");
const reactSinglePageAppRoot = document.getElementById("root");
const reactJobPicker = document.getElementById("reactJobPickerPlaceholder");
const subTradeAgreement = document.getElementById("subTradeAgreement");

// Please do not add any more code to this file, do full react conversion instead.

if (reactMainNavigation) {
    ReactDOM.render(
        <MemoryRouter>
            <CompatRouter>
                <ErrorBoundary>
                    <BTSuspense>
                        <Route
                            render={(props) => (
                                <FocusProvider shareFocusWithParent>
                                    <MainNavigationWrapper
                                        render={(state) => (
                                            <>
                                                <MainNavigation
                                                    fromReact={false}
                                                    {...props}
                                                    {...state}
                                                />
                                            </>
                                        )}
                                    />
                                </FocusProvider>
                            )}
                        />
                    </BTSuspense>
                </ErrorBoundary>
            </CompatRouter>
        </MemoryRouter>,
        reactMainNavigation,
    );
}

if (reactJobPicker) {
    ReactDOM.render(
        <ErrorBoundary>
            <div className="jobPickerMemoryRouter">
                <MemoryRouter>
                    <CompatRouter>
                        <AppProvider>
                            <BTSuspense>
                                <Route render={(props) => <JobPickerWrapper {...props} />} />
                            </BTSuspense>
                        </AppProvider>
                    </CompatRouter>
                </MemoryRouter>
            </div>
        </ErrorBoundary>,
        reactJobPicker,
    );
}

if (reactHeaderInfo) {
    ReactDOM.render(
        <MemoryRouter>
            <CompatRouter>
                <ErrorBoundary>
                    <BTSuspense>
                        <Route
                            render={(props) => (
                                <MainNavigationWrapper
                                    render={(state, onSetSelectedJobId) => (
                                        <>
                                            <ShowOnPortal
                                                owner
                                                render={() => (
                                                    <OwnerHeaderInfo
                                                        key={state.headerKey}
                                                        setSelectedJobId={onSetSelectedJobId}
                                                        {...props}
                                                        {...state}
                                                    />
                                                )}
                                            />
                                            <UserInfoContext.Consumer>
                                                {(userContext) => {
                                                    return (
                                                        <ModalLauncher
                                                            search={window.location.search}
                                                            {...props}
                                                            userInfo={userContext}
                                                        />
                                                    );
                                                }}
                                            </UserInfoContext.Consumer>
                                            {/* eslint-disable-next-line react/forbid-elements */}
                                            <Suspense fallback={<></>}>
                                                <HeaderAlerter {...props} {...state} />
                                            </Suspense>
                                        </>
                                    )}
                                />
                            )}
                        />
                    </BTSuspense>
                </ErrorBoundary>
            </CompatRouter>
        </MemoryRouter>,
        reactHeaderInfo,
    );
}

const hasBeenViewed = BTSessionStorage.get("bt-boolean-hasViewedTradeAgreement");
const tradeAgreementValues = BTSessionStorage.get("bt-object-tradeAgreementValues");
if (subTradeAgreement && !hasBeenViewed && !isNullOrUndefined(tradeAgreementValues)) {
    // eslint-disable-next-line deprecate/member-expression, no-restricted-syntax
    const qs = queryString.parse((window as any).location.search);
    const openSubSetup = qs.openSubSetup;
    if (openSubSetup !== "true") {
        ReactDOM.render(
            <MemoryRouter>
                <CompatRouter>
                    <ErrorBoundary>
                        <BTSuspense>
                            <Route
                                render={(props) => (
                                    <MainNavigationWrapper
                                        render={() => (
                                            <BTSuspense>
                                                <TradeAgreement
                                                    {...props}
                                                    builderId={tradeAgreementValues.builderId}
                                                    subId={tradeAgreementValues.subId}
                                                    modalConfig={{
                                                        parentRoute: props.match.url,
                                                        beforeClose: () => {},
                                                    }}
                                                />
                                            </BTSuspense>
                                        )}
                                    />
                                )}
                            />
                        </BTSuspense>
                    </ErrorBoundary>
                </CompatRouter>
            </MemoryRouter>,
            subTradeAgreement,
        );
    }
}

// Start List Page Rendering

// TODO: Remove all instances of useStickyHeaderWithMainNav(); once we are on SPA.

/**
 * Helper function to wrap an AppProvider, MemoryRouter, and Route around your component and render it on a target.
 * Helper function to wrap an AppProvider, MemoryRouter, ErrorBoundary, BTSuspense, and Route around your component and render it on a target.
 * @param component Function to render your lazily loaded component. Will be passed into the component argument of a Route
 * @param path Route to render your component under. This should be the generic route for all portals
 * @param url Route to render your component under. This should be the specific generated route for your portal.
 * @param target HTML element to render the React code in. This should be the placeholder that was created in the webforms page
 */
const renderListPage = (
    component: React.ComponentType<RouteComponentProps<any, StaticContext, any>>,
    path: string,
    url: string,
    target: HTMLElement,
    useUrlSearch?: boolean,
) => {
    const initialLocation = {
        pathname: url,
        search: useUrlSearch ? (url.split("?")[1] ?? "") : window.location.search,
    };

    ReactDOM.render(
        <ErrorBoundary>
            <AppProvider sendPageView>
                <MemoryRouter initialEntries={[initialLocation]} initialIndex={0}>
                    <CompatRouter>
                        <RedirectForCredentialUpgrade />
                        <FocusProvider shareFocusWithParent hotkeysToRemove={["helpCenter"]}>
                            <BTSuspense>
                                <Route path={path} component={component} />
                            </BTSuspense>
                        </FocusProvider>
                    </CompatRouter>
                </MemoryRouter>
            </AppProvider>
        </ErrorBoundary>,
        target,
    );
};

const btAdminContactsTableDiv = document.getElementById("btAdminContactsTableDiv");
if (btAdminContactsTableDiv) {
    const ContactsListPage = (reactRouterProps: RouteComponentProps) => {
        return (
            <EntityListWrapper
                render={(props) => <BtAdminContactsTable {...props} {...reactRouterProps} />}
                isJobPickerRequired={false}
            />
        );
    };

    const initialPath = routes.notFound.getDetailsLink();
    renderListPage(ContactsListPage, routes.notFound.details, initialPath, btAdminContactsTableDiv);
}

const btAdminMobileImpersonationQRDiv = document.getElementById("btAdminMobileImpersonationQRDiv");
if (btAdminMobileImpersonationQRDiv) {
    ReactDOM.render(
        <ErrorBoundary>
            <AppProvider>
                <BTSuspense>
                    <AdminBuildersImpersonationQR />
                </BTSuspense>
            </AppProvider>
        </ErrorBoundary>,
        btAdminMobileImpersonationQRDiv,
    );
}

const btAdminBuilderSearchDropdownDiv = document.getElementById("btAdminBuilderSearchDropdownDiv");
if (btAdminBuilderSearchDropdownDiv) {
    const BuilderSearchDropdownPage = () => {
        return <AdminBuildersSearch data-testid="BTAdmin-admin-builders-builder-search" />;
    };

    const initialPath = routes.notFound.getDetailsLink();
    renderListPage(
        BuilderSearchDropdownPage,
        routes.notFound.details,
        initialPath,
        btAdminBuilderSearchDropdownDiv,
    );
}
const divReactHeaderMenu = document.getElementById("divReactHeaderMenu");
if (divReactHeaderMenu) {
    ReactDOM.render(
        <BTSuspense>
            <BTAdminWebformsMenu />
        </BTSuspense>,
        divReactHeaderMenu,
    );
}

// Please do not add any more code to this file, do full react conversion instead.

// End List Page Rendering

if (isSinglePageApp && reactSinglePageAppRoot) {
    ReactDOM.render(<App />, reactSinglePageAppRoot);
}
